import { api } from "@convex/_generated/api";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

import { SalesCategoryNameField } from "~/components/settings/sales-category-form-fields";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Form } from "~/components/ui/form";
import { convexClient } from "~/integrations/convex/provider";
import { logger } from "~/lib/logger";

const createSalesCategoryRef = api.salesCategories.create;

// V4.1: Search params for pre-filling category name and passing back to parent
const searchParams = z.object({
  name: z.string().optional(),
  deletingId: z.string(),
  deletingName: z.string(),
});

export const Route = createFileRoute(
  "/_authenticated/settings/sales-categories/$id/delete/create-category"
)({
  component: CreateSalesCategoryModal,
  validateSearch: searchParams,
});

interface SalesCategoryFormData {
  name: string;
}

function CreateSalesCategoryModal() {
  const { id } = Route.useParams();
  const { name, deletingId: _deletingId, deletingName } = Route.useSearch();
  const navigate = useNavigate();

  const form = useForm<SalesCategoryFormData>({
    defaultValues: {
      name: name ?? "",
    },
  });

  const createSalesCategoryMutation = useMutation({
    mutationFn: (data: { name: string }) => {
      return convexClient.mutation(createSalesCategoryRef, data);
    },
  });

  // V4.1: Navigate back to parent delete route with created ID in search params
  const handleClose = () => {
    void navigate({
      to: `/settings/sales-categories/${id}/delete`,
      search: { name: deletingName },
    });
  };

  const handleSubmit = async (values: SalesCategoryFormData) => {
    if (createSalesCategoryMutation.isPending) {
      return;
    }

    try {
      const createdId = await createSalesCategoryMutation.mutateAsync(values);
      toast.success("Sales Category added successfully");
      // V4.1: Pass created ID back via search params
      await navigate({
        to: `/settings/sales-categories/${id}/delete`,
        search: {
          name: deletingName,
          newSalesCategoryId: createdId,
        },
      });
    } catch (error) {
      toast.error("Error adding Sales Category");
      logger.exception("Failed to create sales category", error);
    }
  };

  return (
    <Dialog onOpenChange={handleClose} open>
      <DialogContent
        className="w-full max-w-120"
        noCloseButton
        noScrollPrevention
      >
        <DialogHeader className="items-center space-y-0">
          <DialogTitle>Add Sales Category</DialogTitle>
          <DialogDescription className="text-base">
            Create a new sales category for your items.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form
            className="space-y-5"
            onSubmit={form.handleSubmit(handleSubmit)}
          >
            <SalesCategoryNameField
              control={form.control}
              disabled={createSalesCategoryMutation.isPending}
            />

            <div className="grid grid-cols-2 gap-3">
              <Button
                onClick={handleClose}
                size="lg"
                type="button"
                variant="outline"
              >
                Cancel
              </Button>
              <Button
                disabled={createSalesCategoryMutation.isPending}
                size="lg"
                type="submit"
              >
                Add
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
